/**
 * Chapter 2 Cue Choice Component - Rainbow Tail (Emotional Awareness)
 *
 * Trauma-informed emotional awareness activity offering two approaches:
 * Option 1: "I Feel It, I Name It" — verbally name a feeling out loud (original exercise)
 * Option 2: "Color Drift" — body-sensation approach where the child points to a color
 *            that matches their inner feeling, with no words required
 *
 * Based on the same dual-path architecture as BreathingCueChoice (Chapter 1).
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { X, MessageCircle, Palette, Info, Rainbow } from "lucide-react";

interface Chapter2CueChoiceProps {
  onComplete: () => void;
  isCompleted: boolean;
  trackButton?: (buttonId: string, metadata?: any) => void;
}

/** Shows one of the two activity modes step-by-step */
function ActivityMode({ mode, onBack, onComplete }: { mode: 'name' | 'color'; onBack: () => void; onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);

  const nameSteps = [
    "Take a breath and check in with yourself.",
    "Notice: what feeling is showing up right now?",
    "Chant together: \"I feel it, I name it!\"",
    "Point to a color nearby — which one matches your feeling?",
    "Say out loud: \"I feel [feeling].\""
  ];

  const colorSteps = [
    "Look around the room slowly.",
    "Which color catches your eye right now?",
    "Gently point to that color — no need to explain why.",
    "Just notice how your body feels when you look at it.",
    "That's your feeling showing up in color. Perfect."
  ];

  const steps = mode === 'name' ? nameSteps : colorSteps;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-purple-800">
          {mode === 'name' ? (
            <><MessageCircle className="w-5 h-5 text-purple-500" /> I Feel It, I Name It</>
          ) : (
            <><Palette className="w-5 h-5 text-pink-500" /> Color Drift</>
          )}
        </div>
      </div>

      <div className="bg-purple-50 rounded-lg p-6 min-h-[120px] flex items-center justify-center border-2 border-purple-200">
        <AnimatePresence mode="wait">
          <motion.p
            key={currentStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-xl text-gray-800 text-center leading-relaxed"
          >
            {steps[currentStep]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Animated visual */}
      <div className="flex justify-center">
        <motion.div
          className={`w-20 h-20 rounded-full ${
            mode === 'name'
              ? 'bg-gradient-to-br from-purple-400 to-pink-400'
              : 'bg-gradient-to-br from-yellow-400 via-pink-400 to-purple-400'
          }`}
          animate={{ scale: [1, 1.15, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-2">
        {steps.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep ? 'bg-purple-600 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-800">
          ← Back to choices
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.max(0, p - 1))} disabled={currentStep === 0} className="text-sm">Previous</Button>
          <Button variant="outline" onClick={() => setCurrentStep(p => Math.min(steps.length - 1, p + 1))} disabled={currentStep === steps.length - 1} className="text-sm">Next</Button>
        </div>
      </div>

      {currentStep === steps.length - 1 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center pt-2">
          <Button onClick={onComplete} className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-base font-medium">
            {mode === 'name' ? 'I named my feeling!' : 'I found my color!'}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Parent/Educator info panel */
function EducatorInfo({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 space-y-3"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">For Parents & Educators</h4>
        </div>
        <button onClick={onClose} className="text-blue-400 hover:text-blue-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="text-sm text-blue-800 space-y-2">
        <p><strong>Why two approaches to naming feelings?</strong></p>
        <p>Some children aren't yet ready—or feel safe—putting big feelings into words. Verbal labeling can feel exposing or pressuring for children who have learned to keep emotions private.</p>
        <p><strong>"Color Drift"</strong> offers a non-verbal path: finding a color that resonates with an inner state is a gentle, projective technique that sidesteps the pressure to name. Research supports color-emotion association as a valid bridge to emotional literacy.</p>
        <p>Let the child choose what feels comfortable. Both paths build the same skill—emotional self-awareness.</p>
      </div>
    </motion.div>
  );
}

/** Main Chapter 2 Cue Choice Component */
export function Chapter2CueChoice({ onComplete, isCompleted, trackButton }: Chapter2CueChoiceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<'name' | 'color' | null>(null);
  const [showEducatorInfo, setShowEducatorInfo] = useState(false);

  const handleClose = () => {
    setSelectedMode(null);
    setShowEducatorInfo(false);
    setIsOpen(false);
  };

  const handleComplete = () => {
    onComplete();
    handleClose();
  };

  return (
    <>
      {/* Glowing trigger button */}
      <motion.button
        className="relative bg-purple-100 p-8 rounded-full border-4 border-white/70 shadow-2xl backdrop-blur-sm w-24 h-24 flex items-center justify-center"
        onClick={() => {
          trackButton?.('rainbow_tail_cue_open', { cue_name: 'Rainbow Tail' });
          setIsOpen(true);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          scale: [1, 1.25, 1],
          boxShadow: [
            "0 0 0 0 rgba(168, 85, 247, 0.7)",
            "0 0 0 30px rgba(168, 85, 247, 0)",
            "0 0 0 0 rgba(168, 85, 247, 0.7)",
          ],
        }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Rainbow className="w-12 h-12 text-purple-600" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-gray-200 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 border-2 border-current flex items-center justify-center">
                    <Rainbow className="w-5 h-5" />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
                <div className="text-center px-16">
                  <h3 className="text-xl font-bold">
                    <span className="font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 bg-clip-text text-transparent">Rainbow Feeling</span>
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <AnimatePresence mode="wait">
                  {showEducatorInfo && <EducatorInfo onClose={() => setShowEducatorInfo(false)} />}

                  {!selectedMode && !showEducatorInfo && (
                    <motion.div key="choice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                      <p className="text-center text-gray-700 mb-2">Choose the path that feels right in this moment:</p>
                      <button onClick={() => setShowEducatorInfo(true)} className="flex items-center gap-1 mx-auto text-sm text-blue-600 hover:text-blue-800 mb-4">
                        <Info className="w-4 h-4" /> For Parents/Educators
                      </button>

                      {/* Option 1 */}
                      <motion.button
                        onClick={() => { trackButton?.('rainbow_path_name', { cue_name: 'Rainbow Tail' }); setSelectedMode('name'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-xl text-left hover:border-purple-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-purple-900 mb-1">I Feel It, I Name It</h4>
                            <p className="text-sm text-purple-700">Chant, point to a feeling color, and say your feeling out loud</p>
                          </div>
                        </div>
                      </motion.button>

                      {/* Option 2 */}
                      <motion.button
                        onClick={() => { trackButton?.('rainbow_path_color', { cue_name: 'Rainbow Tail' }); setSelectedMode('color'); }}
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        className="w-full p-6 bg-gradient-to-br from-pink-50 to-yellow-50 border-2 border-pink-300 rounded-xl text-left hover:border-pink-400 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                            <Palette className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-pink-900 mb-1">Color Drift</h4>
                            <p className="text-sm text-pink-700">Find a color in the room that feels like how you feel—no words needed, just point</p>
                          </div>
                        </div>
                      </motion.button>

                      <p className="text-center text-sm text-gray-500 mt-4">Both are perfect ways to notice your feelings!</p>
                    </motion.div>
                  )}

                  {selectedMode && !showEducatorInfo && (
                    <ActivityMode key={selectedMode} mode={selectedMode} onBack={() => setSelectedMode(null)} onComplete={handleComplete} />
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
